import { invariant } from '@seen/core/invariant'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface BradenFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /**
   * AI-staged answers to pre-populate an unsubmitted form (see
   * `useValidatedToolForm`). Does not disable the form or hide submit.
   */
  prefillValuesByLinkId?: Record<string, string>
  /**
   * Build the per-field AI suggestion overlay (highlight plus the option's own
   * Accept/Reject/Undo controls), keyed by linkId, given this form's suggestion
   * `setValue` so a decision can seed or blank the field. Threaded from the node
   * view; absent for an unsuggested form. The tool-HEADER controls are not here:
   * they carry no instrument knowledge, so `renderSuggestionControls` rides
   * `...delegated` straight into `ValidatedToolInnerForm`.
   */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  'sensory-perception': z
    .enum([
      'completely-limited',
      'very-limited',
      'slightly-limited',
      'no-impairment',
    ])
    .nullable(),
  moisture: z
    .enum([
      'constantly-moist',
      'very-moist',
      'occasionally-moist',
      'rarely-moist',
    ])
    .nullable(),
  activity: z
    .enum(['bedfast', 'chairfast', 'walks-occasionally', 'walks-frequently'])
    .nullable(),
  mobility: z
    .enum([
      'completely-immobile',
      'very-limited',
      'slightly-limited',
      'no-limitations',
    ])
    .nullable(),
  nutrition: z
    .enum(['very-poor', 'probably-inadequate', 'adequate', 'excellent'])
    .nullable(),
  'friction-and-shear': z
    .enum(['problem', 'potential-problem', 'no-apparent-problem'])
    .nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.bradenQuestionnaire)

export function BradenForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: BradenFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })

  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For predicting pressure sore risk"
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection
        title="Sensory Perception"
        subtitle="Ability to respond meaningfully to pressure-related discomfort"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="sensory-perception"
          suggestion={fieldSuggestions?.['sensory-perception']}
          options={[
            {
              value: 'completely-limited',
              label: '1. Completely limited',
              description:
                'Unresponsive (does not moan, flinch, or grasp) to painful stimuli, due to diminished level of consciousness or sedation, OR limited ability to feel pain over most of body surface.',
            },
            {
              value: 'very-limited',
              label: '2. Very limited',
              description:
                'Responds only to painful stimuli. Cannot communicate discomfort except by moaning or restlessness, OR has a sensory impairment which limits the ability to feel pain or discomfort over ½ of body.',
            },
            {
              value: 'slightly-limited',
              label: '3. Slightly limited',
              description:
                'Responds to verbal commands but cannot always communicate discomfort or need to be turned, OR has some sensory impairment which limits ability to feel pain or discomfort in 1 or 2 extremities.',
            },
            {
              value: 'no-impairment',
              label: '4. No impairment',
              description:
                'Responds to verbal commands. Has no sensory deficit which would limit ability to feel or voice pain or discomfort.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Moisture"
        subtitle="Degree to which skin is exposed to moisture"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="moisture"
          suggestion={fieldSuggestions?.moisture}
          options={[
            {
              value: 'constantly-moist',
              label: '1. Constantly moist',
              description:
                'Skin is kept moist almost constantly by perspiration, urine, etc. Dampness is detected every time patient is moved or turned.',
            },
            {
              value: 'very-moist',
              label: '2. Often moist',
              description:
                'Skin is often but not always moist. Linen must be changed at least once a shift.',
            },
            {
              value: 'occasionally-moist',
              label: '3. Occasionally moist',
              description:
                'Skin is occasionally moist, requiring an extra linen change approximately once a day.',
            },
            {
              value: 'rarely-moist',
              label: '4. Rarely moist',
              description:
                'Skin is usually dry; linen only requires changing at routine intervals.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Activity"
        subtitle="Degree of physical activity"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="activity"
          suggestion={fieldSuggestions?.activity}
          options={[
            {
              value: 'bedfast',
              label: '1. Bedfast',
              description: 'Confined to bed.',
            },
            {
              value: 'chairfast',
              label: '2. Chairfast',
              description:
                'Ability to walk severely limited or nonexistent. Cannot bear own weight and/or must be assisted into chair or wheelchair.',
            },
            {
              value: 'walks-occasionally',
              label: '3. Walks occasionally',
              description:
                'Walks occasionally during day, but for very short distances, with or without assistance. Spends majority of each shift in bed or chair.',
            },
            {
              value: 'walks-frequently',
              label: '4. Walks frequently',
              description:
                'Walks outside the room at least twice a day and inside room at least once every 2 hours during waking hours.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Mobility"
        subtitle="Ability to change and control body position"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="mobility"
          suggestion={fieldSuggestions?.mobility}
          options={[
            {
              value: 'completely-immobile',
              label: '1. Completely immobile',
              description:
                'Does not make even slight changes in body or extremity position without assistance.',
            },
            {
              value: 'very-limited',
              label: '2. Very limited',
              description:
                'Makes occasional slight changes in body or extremity position but unable to make frequent or significant changes independently.',
            },
            {
              value: 'slightly-limited',
              label: '3. Slightly limited',
              description:
                'Makes frequent though slight changes in body or extremity position independently.',
            },
            {
              value: 'no-limitations',
              label: '4. No limitations',
              description:
                'Makes major and frequent changes in position without assistance.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Nutrition"
        subtitle="Usual food intake pattern"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="nutrition"
          suggestion={fieldSuggestions?.nutrition}
          options={[
            {
              value: 'very-poor',
              label: '1. Very poor',
              description:
                'Never eats a complete meal. Rarely eats more than 1/3 of any food offered. Eats 2 servings or less of protein (meat or dairy products) per day. Takes fluids poorly. Does not take a liquid dietary supplement, OR is NPO (nothing by mouth) and/or maintained on clear liquids or IV (intravenously) for more than 5 days.',
            },
            {
              value: 'probably-inadequate',
              label: '2. Probably inadequate',
              description:
                'Rarely eats a complete meal and generally eats only about ½ of any food offered. Protein intake includes only 3 servings of meat or dairy products per day. Occasionally will take a dietary supplement OR receives less than optimum amount of liquid diet or tube feeding.',
            },
            {
              value: 'adequate',
              label: '3. Adequate',
              description:
                'Eats over half of most meals. Eats a total of 4 servings of protein (meat, dairy products) each day. Occasionally refuses a meal, but will usually take a supplement if offered, OR is on a tube feeding or TPN (total parenteral nutrition) regimen, which probably meets most of nutritional needs.',
            },
            {
              value: 'excellent',
              label: '4. Excellent',
              description:
                'Eats most of every meal. Never refuses a meal. Usually eats a total of 4 or more servings of meat and dairy products. Occasionally eats between meals. Does not require supplementation.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Friction and Shear">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="friction-and-shear"
          suggestion={fieldSuggestions?.['friction-and-shear']}
          options={[
            {
              value: 'problem',
              label: '1. Problem',
              description:
                'Requires moderate to maximum assistance in moving. Complete lifting without sliding against sheets is impossible. Frequently slides down in bed or chair, requiring frequent repositioning with maximum assistance. Spasticity, contractures, or agitation leads to almost constant friction.',
            },
            {
              value: 'potential-problem',
              label: '2. Potential problem',
              description:
                'Moves feebly or requires minimum assistance. During a move, skin probably slides to some extent against sheets, chair, restraints, or other devices. Maintains relatively good position in chair or bed most of the time but occasionally slides down.',
            },
            {
              value: 'no-apparent-problem',
              label: '3. No apparent problem',
              description:
                'Moves in bed and in chair independently and has sufficient muscle strength to lift up completely during move. Maintains good position in bed or chair at all times.',
            },
          ]}
        />
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

const COLORS = {
  'Mild Risk': 'green',
  'Moderate Risk': 'yellow',
  'High Risk': 'orange',
  'Severe Risk': 'red',
} as const satisfies Record<questionnaires.BradenCategory, string>

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const label = questionnaires.bradenCategory(result.score)
  invariant(label, `Braden score ${result.score} has no category`)
  const color = COLORS[label]
  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
